<template>
  <li
    class="hx-visita"
    :class="['hx-visita-' + estado.tono, { 'is-abierta': abierta }]"
    :data-orden-id="visita._id"
    data-testid="historial-visita"
  >
    <h3 class="hx-visita-encabezado">
      <button
        :id="'visita-boton-' + visita._id"
        type="button"
        class="hx-visita-boton"
        :aria-expanded="String(abierta)"
        :aria-controls="'visita-detalle-' + visita._id"
        data-testid="historial-visita-boton"
        @click="$emit('alternar', visita._id)"
      >
        <span class="hx-visita-fecha">
          {{ fecha(visita.recibida) || 'Sin fecha' }}
          <small v-if="km">{{ km }} km</small>
        </span>
        <span class="hx-visita-que">
          <span class="hx-visita-titulo">{{ detalle || 'Sin descripción del trabajo' }}</span>
          <span class="hx-visita-meta">
            <span class="hx-mono">{{ visita.folio }}</span>
            <span class="hx-chip" :class="'hx-chip-' + estado.tono" data-testid="historial-visita-estado">{{ estado.texto }}</span>
            <span v-if="visita.aTiempo === true" class="hx-chip hx-chip-exito" data-testid="historial-visita-puntualidad">A tiempo</span>
            <span v-else-if="visita.aTiempo === false" class="hx-chip hx-chip-aviso" data-testid="historial-visita-puntualidad">Con retraso</span>
            <span v-if="reingreso" class="hx-chip hx-chip-aviso" data-testid="historial-visita-reingreso">{{ reingreso }}</span>
            <span v-if="mecanicosCortos" class="hx-visita-mecanicos">{{ mecanicosCortos }}</span>
          </span>
        </span>
        <span class="hx-visita-total" :class="{ 'is-sin-venta': !ventas.length }">{{ total }}</span>
        <AppIcon name="chevron-down" size="h-4 w-4" class="hx-visita-flecha" />
      </button>
    </h3>

    <div
      v-if="abierta"
      :id="'visita-detalle-' + visita._id"
      class="hx-visita-detalle"
      role="region"
      :aria-labelledby="'visita-boton-' + visita._id"
      data-testid="historial-visita-detalle"
    >
      <div v-if="servicios.length" class="hx-bloque">
        <h4 class="hx-bloque-titulo">Servicios y mecánicos</h4>
        <ul class="hx-servicios">
          <li v-for="(servicio, i) in servicios" :key="i">
            <span>{{ servicio.nombre }}</span>
            <span class="hx-texto-suave">{{ servicio.mecanicos || 'Sin mecánico asignado' }}</span>
          </li>
        </ul>
      </div>

      <div v-for="venta in ventas" :key="venta._id || venta.folio" class="hx-bloque">
        <div class="hx-tabla-scroll">
          <table class="hx-lineas" data-testid="historial-visita-lineas">
            <caption>
              Venta <span class="hx-mono">{{ venta.folio || 'sin folio' }}</span>
              <template v-if="venta.factura"> · Factura <span class="hx-mono">{{ venta.factura }}</span></template>
            </caption>
            <thead>
              <tr>
                <th scope="col">Concepto</th>
                <th scope="col">Cant.</th>
                <th scope="col">Importe</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(linea, i) in venta.lineas" :key="i">
                <td>
                  {{ oracion(linea.concepto) || 'Sin concepto' }}
                  <span class="hx-tipo">{{ linea.tipo === 'servicio' ? 'Servicio' : linea.tipo === 'refaccion' ? 'Refacción' : 'Otro' }}</span>
                </td>
                <td>{{ numero(linea.cantidad) }}</td>
                <td>{{ dinero(linea.importe) }}</td>
              </tr>
              <tr v-if="!venta.lineas.length">
                <td colspan="3" class="hx-texto-suave">La venta no tiene conceptos.</td>
              </tr>
            </tbody>
            <tfoot>
              <tr data-testid="historial-venta-subtotal">
                <th scope="row" colspan="2">Subtotal</th>
                <td>{{ dinero(venta.subtotal) }}</td>
              </tr>
              <tr v-if="venta.iva" data-testid="historial-venta-iva">
                <th scope="row" colspan="2">IVA</th>
                <td>{{ dinero(venta.iva) }}</td>
              </tr>
              <tr v-if="venta.ajuste" data-testid="historial-venta-ajuste">
                <th scope="row" colspan="2">Ajuste</th>
                <td>{{ dinero(venta.ajuste) }}</td>
              </tr>
              <tr class="hx-lineas-total" data-testid="historial-venta-total">
                <th scope="row" colspan="2">Total</th>
                <td>{{ dinero(venta.total) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <p v-if="!ventas.length" class="hx-texto-suave">Esta orden no tiene una venta ligada.</p>

      <p v-if="notaFinal" class="hx-nota"><b>Nota final:</b> {{ notaFinal }}</p>

      <dl class="hx-visita-pie">
        <div><dt>Recibida</dt><dd>{{ fecha(visita.recibida) || 'Sin fecha' }}</dd></div>
        <div><dt>Prometida</dt><dd>{{ fecha(visita.prometida) || 'Sin fecha' }}</dd></div>
        <div><dt>Entregada</dt><dd>{{ fecha(visita.entregada) || 'Sin entregar' }}</dd></div>
        <div v-if="foliosVenta"><dt>{{ ventas.length > 1 ? 'Ventas' : 'Venta' }}</dt><dd class="hx-mono">{{ foliosVenta }}</dd></div>
        <div><dt>Factura</dt><dd :class="{ 'hx-mono': visita.factura }">{{ visita.factura || 'Sin factura' }}</dd></div>
      </dl>
    </div>
  </li>
</template>

<script>
/*
 * Una visita (orden de servicio) del expediente. Se despliega en su lugar,
 * sin modales: servicios y mecánicos, líneas de cada venta, nota final y
 * fechas de recepción, promesa y entrega.
 */
import AppIcon from '@/components/ui/AppIcon.vue'
import { formatearDinero, formatearFechaNumerica, formatearNumero, textoOracion } from '@/utils/formato'
import { estadoDeOrden, nombreCorto, nombreLimpio } from '@/utils/historial'

const centavos = (valor) => Math.round(valor * 100) / 100
const sinCentavosSueltos = (valor) => (Math.abs(valor) >= 0.01 ? valor : 0)
function numeroONulo(valor) {
  if (valor === null || valor === undefined || valor === '') return null
  const numero = Number(valor)
  return Number.isFinite(numero) ? numero : null
}

export default {
  name: 'VisitaOrden',
  components: { AppIcon },
  props: {
    visita: { type: Object, required: true },
    abierta: { type: Boolean, default: false },
  },
  emits: ['alternar'],
  computed: {
    estado() {
      return estadoDeOrden(this.visita.estado)
    },
    detalle() {
      return textoOracion(this.visita.detalle)
    },
    notaFinal() {
      return textoOracion(this.visita.notaFinal)
    },
    km() {
      return Number(this.visita.km) > 0 ? formatearNumero(this.visita.km, { decimales: 0 }) : ''
    },
    reingreso() {
      const dias = this.visita.reingreso && Number(this.visita.reingreso.diasDesdeAnterior)
      if (!Number.isFinite(dias) || dias < 0) return ''
      if (dias === 0) return 'Regresó el mismo día'
      return `Regresó ${dias} ${dias === 1 ? 'día' : 'días'} después`
    },
    servicios() {
      return (Array.isArray(this.visita.servicios) ? this.visita.servicios : []).map((servicio) => ({
        nombre: textoOracion(servicio && servicio.nombre) || 'Servicio sin nombre',
        mecanicos: (servicio && Array.isArray(servicio.mecanicos) ? servicio.mecanicos : [])
          .map((m) => nombreLimpio(m))
          .filter(Boolean)
          .join(', '),
      }))
    },
    mecanicosCortos() {
      const nombres = (Array.isArray(this.visita.servicios) ? this.visita.servicios : [])
        .flatMap((servicio) => (servicio && Array.isArray(servicio.mecanicos) ? servicio.mecanicos : []))
        .map((m) => nombreCorto(m))
        .filter(Boolean)
      return [...new Set(nombres)].join(', ')
    },
    /*
     * Subtotal e IVA vienen de la venta guardada (`subtotal`, `iva`) y se usan
     * tal cual. Si faltan: Subtotal = suma de líneas (precio × cantidad, sin
     * IVA) e IVA = total − subtotal. La fila de IVA se omite si es 0. Si
     * subtotal + IVA no da el total (descuentos, redondeos de captura), la
     * diferencia sale como «Ajuste» para que la cuenta cuadre.
     */
    ventas() {
      return (Array.isArray(this.visita.ventas) ? this.visita.ventas : []).filter(Boolean).map((venta) => {
        const lineas = Array.isArray(venta.lineas) ? venta.lineas : []
        const total = numeroONulo(venta.total)
        const subtotal = numeroONulo(venta.subtotal) ?? centavos(lineas.reduce((suma, linea) => suma + (Number(linea && linea.importe) || 0), 0))
        const ivaGuardado = numeroONulo(venta.iva)
        const iva = ivaGuardado ?? (total === null ? 0 : Math.max(0, centavos(total - subtotal)))
        const ajuste = total === null ? 0 : centavos(total - subtotal - iva)
        return { ...venta, lineas, subtotal, iva: sinCentavosSueltos(iva), ajuste: sinCentavosSueltos(ajuste) }
      })
    },
    foliosVenta() {
      return this.ventas.map((venta) => venta.folio).filter(Boolean).join(', ')
    },
    total() {
      return this.ventas.length ? formatearDinero(this.visita.total) || formatearDinero(0) : 'Sin venta'
    },
  },
  methods: {
    // Fechas sin hora o con la hora local guardada como UTC: se leen en UTC
    fecha(valor) {
      return formatearFechaNumerica(valor, { utc: true })
    },
    dinero(valor) {
      return formatearDinero(valor) || 'Sin dato'
    },
    numero(valor) {
      return formatearNumero(valor) || '—'
    },
    oracion(valor) {
      return textoOracion(valor)
    },
  },
}
</script>
